/*
 * Hvattum Studios — campaign hero film.
 *
 * Progressive enhancement only. The poster and text are rendered server-side
 * and remain complete if this script never runs. The film lives in an inert
 * <template> until this element injects it, so no video bytes are requested
 * during HTML parsing.
 *
 * Independent of Dawn's DeferredMedia and pauseAllMedia by design: the poster
 * is never removed, and pausing is scoped to this element alone.
 *
 * The film is decorative and always muted. It is revealed only once it is
 * genuinely playing, so a browser that declines autoplay — iPhone Low Power
 * Mode, Android Battery Saver or Data Saver, a restrictive setting — leaves
 * the poster on screen with the Play control offering a way in, rather than
 * showing a frozen first frame that reads as a static photograph.
 */
class HvattumHeroFilm extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.mobileQuery = window.matchMedia('(max-width: 749px)');
    this.video = null;
    // A deliberate pause outranks every automatic resume below.
    this.userPaused = false;

    if (!this.selectTemplate()) return;

    this.control = this.querySelector('[data-hvattum-film-toggle]');
    this.controlLabel = this.querySelector('[data-hvattum-film-toggle-label]');
    if (this.control) {
      this.control.hidden = false;
      this.control.addEventListener('click', () => this.onToggle());
      this.syncControl();
    }

    // Autoplay is only ever attempted when the viewport allows it and motion
    // is welcome. Otherwise nothing is fetched until the customer asks for it.
    this.autoplayIntended = this.autoplayAllowed() && !this.reducedMotion.matches;

    document.addEventListener('visibilitychange', () => this.onVisibilityChange());

    if (this.autoplayIntended) this.whenPageLoaded(() => this.observeViewport());
  }

  selectTemplate() {
    const mobile = this.querySelector('[data-hvattum-film="mobile"]');
    const main = this.querySelector('[data-hvattum-film="main"]');
    // The main film is used on every device unless a mobile film is provided.
    this.template = this.mobileQuery.matches && mobile ? mobile : main;
    return Boolean(this.template);
  }

  autoplayAllowed() {
    const attribute = this.mobileQuery.matches ? 'autoplayMobile' : 'autoplay';
    return this.dataset[attribute] !== 'false';
  }

  whenPageLoaded(callback) {
    if (document.readyState === 'complete') {
      callback();
    } else {
      window.addEventListener('load', callback, { once: true });
    }
  }

  observeViewport() {
    if (!('IntersectionObserver' in window)) {
      this.injectFilm(true);
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.injectFilm(true);
      },
      { rootMargin: '200px 0px' }
    );

    this.observer.observe(this);
  }

  /* Builds the video and, when asked, requests playback. Returns the element
     so callers can await the play attempt. */
  injectFilm(autoplay) {
    if (this.video || !this.template) return null;

    const fragment = this.template.content.cloneNode(true);
    const video = fragment.querySelector('video');
    if (!video) return null;

    // Muted must be a property, not just an attribute, and must be set before
    // any play() request — several browsers only grant inline playback then.
    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.removeAttribute('controls');
    // Metadata only: the poster is the immediate visual, and the film streams
    // from its faststart index rather than being fetched whole up front.
    video.preload = 'metadata';
    // Letting the browser run its own autoplay algorithm as well as our
    // play() call is what starts the fetch on devices that will not buffer
    // for a script alone. It is a single attempt, not a retry loop.
    if (autoplay) video.autoplay = true;

    // The film carries no information that the page does not already convey.
    // The control is its accessible surface, so the video itself stays out of
    // the accessibility tree.
    video.setAttribute('aria-hidden', 'true');
    video.tabIndex = -1;

    // Still detached from the document, so nothing here costs a request:
    // drop video_tag's own poster and <img> fallback. The section's <picture>
    // is the single poster and the single failure fallback.
    video.removeAttribute('poster');
    const fallbackImage = video.querySelector('img');
    if (fallbackImage) fallbackImage.remove();

    // Reveal follows real playback, never mere readiness — this is what keeps
    // a refused autoplay looking like a poster instead of a frozen frame.
    video.addEventListener('playing', () => this.onPlaying());
    video.addEventListener('play', () => this.syncControl());
    video.addEventListener('pause', () => this.syncControl());
    video.addEventListener('error', () => this.handleFailure(), { once: true });
    // A failing <source> fires its error on the source element, not on the
    // video, so without this a dead file would leave the control offering to
    // pause something that will never play.
    video.querySelectorAll('source').forEach((source) => {
      source.addEventListener('error', () => this.onSourceError(), { once: true });
    });

    this.video = video;
    this.appendChild(video);
    this.syncControl();

    if (autoplay) this.requestPlay();
    return video;
  }

  /* One attempt. A refusal is recorded and the poster is left in place; the
     control is the way back in, so there is nothing to retry against. */
  requestPlay() {
    if (!this.video) return;
    this.video.muted = true;

    const attempt = this.video.play();
    if (!attempt || typeof attempt.catch !== 'function') return;

    attempt.catch(() => {
      this.removeAttribute('data-film-ready');
      this.syncControl();
    });
  }

  onPlaying() {
    // Revealed only now that frames are actually moving, so the poster is
    // never swapped for a still one.
    this.setAttribute('data-film-ready', 'true');
    this.syncControl();
  }

  /* NETWORK_NO_SOURCE means every candidate has been exhausted, so this is a
     real failure rather than one source of several being skipped. */
  onSourceError() {
    if (!this.video) return;
    if (this.video.networkState === HTMLMediaElement.NETWORK_NO_SOURCE) {
      this.handleFailure();
    }
  }

  handleFailure() {
    // Leave the poster visible; the film is never revealed. The control goes
    // away because there is nothing it could play.
    this.removeAttribute('data-film-ready');
    this.video = null;
    this.syncControl();
    if (this.control) this.control.hidden = true;
  }

  onToggle() {
    // First press under reduced motion, or after autoplay was declined,
    // builds the film and starts it from the customer's own gesture.
    if (!this.video) {
      this.userPaused = false;
      this.injectFilm(false);
      this.requestPlay();
      return;
    }

    if (this.video.paused) {
      this.userPaused = false;
      this.requestPlay();
    } else {
      this.userPaused = true;
      this.video.pause();
    }
  }

  syncControl() {
    if (!this.control) return;
    const playing = Boolean(this.video) && !this.video.paused;
    const label = playing
      ? this.control.dataset.labelPause
      : this.control.dataset.labelPlay;

    if (this.controlLabel && this.controlLabel.textContent !== label) {
      this.controlLabel.textContent = label;
    }
    this.control.setAttribute('data-playing', playing ? 'true' : 'false');
  }

  onVisibilityChange() {
    if (!this.video) return;

    if (document.hidden) {
      this.video.pause();
      return;
    }

    // Never resume over a deliberate pause, or where motion is unwelcome.
    if (this.userPaused || this.reducedMotion.matches) return;
    this.requestPlay();
  }
}

customElements.define('hvattum-hero-film', HvattumHeroFilm);
